import React, { useEffect, useState, MouseEvent } from 'react';
import { connect } from 'react-redux';
import { RootState } from '../../store';
import {
    AddVipUsersToLaundryRoom,
    VipUserAddingReportDto,
    VipUserReport,
    VipUserResult,
    VipUsersForAddingDto,
    resetAddVipUsers,
} from '../../store/vipUsersSlice';

import { RequestStatus } from '../../store/network/managedRequest';
import useTranslate from '../useTranslate';
import EditLargeCardFormik, {
    EditLargeCardFormikProps,
} from '../main/edit-large-card-formik';
import { FormControl } from 'react-bootstrap';
import ValidatorFormGroup from '../main/validator-form-group';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWindowClose } from '@fortawesome/pro-light-svg-icons';
import { PbGenericRequest } from '../../store/pbRequestSlice';
import { PbRequest } from '../../store/pbRequest';

interface Props {
    laundryRoomId: number;

    addReport: VipUserAddingReportDto | undefined;
    addRequest: PbGenericRequest;
    AddVipUsersToLaundryRoom: (
        laundryRoomId: number,
        addVipUsers: VipUsersForAddingDto
    ) => void;
    ResetAddVipUsers: () => void;
}

interface VipUsersForAddingCardValues {
    inputField: string;
}

const initialValues: VipUsersForAddingCardValues = {
    inputField: '',
};

interface AddReportForDisplay {
    successCount: number;
    alreadyExistingEntries: VipUserReport[];
    errorEntries: VipUserReport[];
}

function VipAddUserCard(props: Readonly<Props>) {
    const strings = useTranslate('VipAddUserCard');
    const [addReport, setAddReport] = useState<AddReportForDisplay | undefined>(
        translateAddResult(props.addReport)
    );
    useEffect(() => {
        if (
            props.addRequest.status !== RequestStatus.SUCCESS ||
            !props.addReport
        ) {
            return;
        }
        setAddReport(translateAddResult(props.addReport));
    }, [props.addReport, props.addRequest.status]);

    if (!props.laundryRoomId) {
        return <div>No laundry room id!</div>;
    }

    return (
        <EditLargeCardFormik
            title={strings.title}
            handleSubmit={handleSubmit}
            submitLabel={strings.submit}
            error={props.addRequest.status === RequestStatus.FAILURE}
            initialValues={initialValues}
            validate={validateForm}
            children={renderForm}
        />
    );

    function handleSubmit(values: VipUsersForAddingCardValues) {
        const asList: string[] = splitIntoEntries(values.inputField);
        props.AddVipUsersToLaundryRoom(props.laundryRoomId, {
            customer_ids_or_emails: asList,
        });
    }

    function renderForm(
        formProps: EditLargeCardFormikProps<VipUsersForAddingCardValues>
    ) {
        if (formProps.active) {
            return renderEditState(formProps);
        } else {
            return renderReport();
        }
    }

    function renderReport() {
        return (
            <div>
                {strings.infoText}

                {maybeRenderResult()}
            </div>
        );
    }

    function maybeRenderResult() {
        if (props.addRequest.status !== RequestStatus.SUCCESS) {
            return null;
        }
        if (!addReport) {
            return null;
        }
        return (
            <div>
                <hr className="divider" />
                <div style={{ display: 'flex', flexDirection: 'row' }}>
                    <h4>{strings.reportTitle}</h4>
                    <a onClick={handleReportReset} className={'ml-2'}>
                        <FontAwesomeIcon
                            icon={faWindowClose}
                            className="icon-for-button"
                        />
                        {strings.closeReport}
                    </a>
                </div>
                {addReport.successCount > 0 &&
                    strings._utils.sprintf(
                        strings.successfulSummary,
                        addReport.successCount
                    )}
                {addReport.alreadyExistingEntries.length > 0 &&
                    strings._utils.sprintf(
                        strings.alreadyExistsSummary,
                        addReport.alreadyExistingEntries.length
                    )}
                {maybeRenderError()}
            </div>
        );
    }

    function handleReportReset(e: MouseEvent<HTMLAnchorElement>) {
        e.preventDefault();
        props.ResetAddVipUsers();
    }

    function maybeRenderError() {
        if (addReport.errorEntries.length === 0) {
            return null;
        }

        return (
            <div>
                {strings.errorSummary}
                <ul>
                    {addReport.errorEntries.map((it) => (
                        <li
                            key={`entry-${encodeURIComponent(it.email_or_customer_id)}`}
                        >
                            {it.email_or_customer_id}:{' '}
                            {strings.errors[it.result] || it.result}
                        </li>
                    ))}
                </ul>
            </div>
        );
    }

    function renderEditState(
        formProps: EditLargeCardFormikProps<VipUsersForAddingCardValues>
    ) {
        return (
            <div>
                <div className={'mb-2'}>{strings.explanation}</div>

                <ValidatorFormGroup
                    label={strings.newEntriesLabel}
                    hideErrorLabel={!formProps.errors.inputField}
                    errorLabel={
                        formProps.errors.inputField
                            ? strings.errors[formProps.errors.inputField]
                            : ''
                    }
                >
                    <FormControl
                        componentClass={'textarea'}
                        value={formProps.values.inputField}
                        onChange={formProps.handleChange}
                        name={'inputField'}
                        required
                        bsSize={'lg'}
                    />
                </ValidatorFormGroup>

                {maybeRenderResult()}
            </div>
        );
    }

    function validateForm(values: VipUsersForAddingCardValues) {
        const errors: Partial<{ inputField: string }> = {};

        const asList: string[] = splitIntoEntries(values.inputField);
        const hasDuplicates = new Set(asList).size !== asList.length;
        if (hasDuplicates) {
            errors.inputField = 'DUPLICATE_ENTRIES';
        }

        return errors;
    }
}

function splitIntoEntries(text: string): string[] {
    return text
        .trim()
        .split(/[\s,]+/)
        .filter((it) => it);
}

function translateAddResult(fullReport?: VipUserAddingReportDto) {
    if (!fullReport) {
        return undefined;
    }
    const result: AddReportForDisplay = {
        successCount: 0,
        alreadyExistingEntries: [],
        errorEntries: [],
    };

    fullReport.reports.forEach((it) => {
        switch (it.result) {
            case VipUserResult.SUCCESS:
                result.successCount++;
                break;
            case VipUserResult.ALREADY_EXISTING:
                result.alreadyExistingEntries.push(it);
                break;
            default:
                result.errorEntries.push(it);
                break;
        }
    });

    return result;
}

export default connect(
    (store: RootState) => ({
        addReport: store.vipUsers.addVipUsersReport,
        addRequest: store.pbRequest[PbRequest.POST_ADD_VIP_USERS],
    }),
    { AddVipUsersToLaundryRoom, ResetAddVipUsers: resetAddVipUsers }
)(VipAddUserCard);
